import { RadioButtonProps } from '@progress/kendo-react-inputs';
import {
  UploadOnBeforeRemoveEvent,
  UploadOnRemoveEvent,
  UploadOnStatusChangeEvent,
} from '@progress/kendo-react-upload';
import { produce } from 'immer';
import _ from 'lodash';
import moment from 'moment';
import { Dispatch, SetStateAction } from 'react';
import { HttpStatuses } from '../../../../../common/enums';
import Checkbox from '../../../../../common/widgets/Checkbox';
import DatePicker from '../../../../../common/widgets/DatePicker';
import Dropdown from '../../../../../common/widgets/Dropdown';
import FileInput from '../../../../../common/widgets/FileInput';
import Input from '../../../../../common/widgets/Input';
import RadioGroup from '../../../../../common/widgets/RadioGroup';
import TextArea from '../../../../../common/widgets/TextArea';
import { config } from '../../../config';
import { Dictionary } from '../../../models/Categories';
import { ErrorState } from '../../../models/Errors';
import {
  DependentModel,
  OptionSemanticTypes,
  OptionType,
  ProductOption,
  ProductRecurrence,
} from '../../../models/Options';
import { resources } from '../../../resources/resources';
import { cardFormFactorOptionId } from '../../MyAccount/CardsPage/Common/constants';
export interface DependentFormItem {
  isSelected: boolean;
  dependent: DependentModel;
}
export const buildInitialForm = (
  options: ProductOption[],
  recurrences: ProductRecurrence[],
  translate: any,
  dependents: DependentModel[],
) => {
  const initialForm: Dictionary<any> = {};
  if (dependents && dependents.length > 0) {
    initialForm['dependents'] = dependents.map((dep: DependentModel) => {
      return {
        isSelected: false,
        dependent: dep,
      } as DependentFormItem;
    });
  } else {
    options.forEach(option => {
      if (option.type == OptionType.Checkbox) {
        initialForm[option.id] = [];
      } else if (option.type == OptionType.Text) {
        initialForm[option.id] = option.value;
      } else if (option.type == OptionType.Select) {
        if (option.values?.length == 1) {
          initialForm[option.id] = {
            name: option.values[0].name,
            value: option.values[0].value,
          };
        } else {
          initialForm[option.id] = {
            name: translate(resources.products.select),
            value: -1,
          };
        }
      } else if (option.type == OptionType.File) {
        initialForm[option.id] = [];
      }
    });
  }

  if (recurrences?.length > 0) {
    if (recurrences?.length > 1) {
      initialForm['recurrenceId'] = {
        title: translate(resources.products.select),
        id: -1,
      };
    } else {
      initialForm['recurrenceId'] = {
        title: recurrences[0].title,
        id: recurrences[0].id,
      };
    }
  }

  return initialForm;
};

export const getPriceChangingOptions = (
  form: Dictionary<any>,
  options: ProductOption[],
): Dictionary<any> => {
  const selectedOptions: any = {};

  // don't need to get options when nothing has changed
  if (_.isEmpty(form)) {
    return selectedOptions;
  }

  Object.keys(form).forEach(key => {
    // reccurences and other configs are not relevant
    if (!Number(key)) {
      return;
    }

    const optionId = Number(key);
    const option = options.find(o => o.id === optionId);
    if (option?.semantic === OptionSemanticTypes.Other) {
      return;
    }

    // options that are not selected/ checked are not relevant
    if (form[optionId] === null || form[optionId] === undefined) {
      return;
    }

    // value-based
    if (Number(form[optionId].value) && Number(form[optionId].value) !== -1) {
      selectedOptions[optionId] = form[optionId].value;
    }

    // checkbox
    if (Number(form[optionId])) {
      selectedOptions[optionId] = form[optionId];
    }

    // select lists
    if (Array.isArray(form[optionId]) && form[optionId].length === 1) {
      selectedOptions[optionId] = form[optionId][0];
    }
  });

  return selectedOptions;
};

export const processForm = (
  form: Dictionary<any>,
  options: ProductOption[],
): string => {
  let payload: Dictionary<any> = produce(form, draft => {
    options.forEach(option => {
      if (option.type == OptionType.Select) {
        draft[option.id] =
          draft[option.id].value != -1 ? draft[option.id].value : undefined;
      }

      if (option.type == OptionType.Date && draft[option.id] instanceof Date) {
        draft[option.id] = moment(draft[option.id]).format('YYYY-MM-DD');
      }
    });

    if (draft['recurrenceId']?.id) {
      draft['recurrenceId'] = draft['recurrenceId'].id;
    }

    if (draft.dependents && Array.isArray(draft.dependents)) {
      draft.dependents
        .filter(dep => dep.isSelected)
        .forEach(dep => {
          const { optionId, nationalId } = dep.dependent;
          if (!draft[optionId]) {
            draft[optionId] = [];
          }
          draft[optionId].push(nationalId);
        });
    }

    delete draft.dependents;
  });

  return JSON.stringify(payload);
};

const getOptionLabel = (name: string, isRequired: boolean): string => {
  return _.unescape(`${name}${isRequired ? '*' : ''}`);
};

export const renderCheckBoxOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  const handleClick = (optionValue: any) => {
    setForm(
      produce((draft: any) => {
        if (draft[option.id].includes(optionValue.value)) {
          draft[option.id] = draft[option.id].filter(
            (v: number) => v != optionValue.value,
          );
        } else {
          draft[option.id].push(optionValue.value);
        }
      }),
    );
  };

  return option.values.map(optionValue => {
    return (
      <Checkbox
        label={getOptionLabel(optionValue.name, option.required)}
        key={optionValue.value}
        value={optionValue.value}
        isChecked={form[option.id]?.includes(optionValue.value)}
        onClick={() => handleClick(optionValue)}
        errors={errors?.[option.id]}
        clearErrors={() => clearErrors?.(option.id)}
        disabled={option.isReadonly}
      />
    );
  });
};

export const renderSelectOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  return (
    <Dropdown
      data={option.values}
      dataItemKey="value"
      textField="name"
      label={getOptionLabel(option.title, option.required)}
      value={form[option.id]}
      onChange={e => {
        setForm(
          produce((draft: any) => {
            draft[option.id] = e.value;
          }),
        );
      }}
      errors={errors?.[option.id]}
      clearErrors={() => clearErrors?.(option.id)}
      disabled={option.isReadonly || option.values?.length == 1}
    />
  );
};

export const renderFileOption = (
  setForm: any,
  option: ProductOption,
  productId: number,
  errors?: ErrorState,
  setErrors?: Dispatch<SetStateAction<ErrorState>>,
  clearErrors?: (optionId: number) => void,
) => {
  const onStatusChange = (e: UploadOnStatusChangeEvent) => {
    if (!e.response) {
      return;
    }

    const isOk =
      e.response.status === HttpStatuses.Ok &&
      typeof e.response.response === 'number' &&
      e.affectedFiles?.[0];

    // e.response does not have a 'status' property if the request returns with a validation error
    const isPreconditionFailed =
      e.response.status === undefined &&
      typeof e.response.response === 'object' &&
      e.response.response.status === HttpStatuses.PreconditionFailed;

    const response = e.response.response;

    if (isOk) {
      setForm(
        produce((draft: any) => {
          draft[option.id].push(response);
        }),
      );

      (e.affectedFiles[0] as any).fileId = response;

      clearErrors?.(option.id);
    }

    if (isPreconditionFailed) {
      setErrors?.(
        produce(d => {
          d[option.id] = [...response.data.details];
        }),
      );
    }
  };

  const onBeforeRemove = (e: UploadOnBeforeRemoveEvent) => {
    e.additionalData.fileId = (e.files[0] as any).fileId;
  };

  const onRemove = (e: UploadOnRemoveEvent) => {
    clearErrors?.(option.id);

    if (e.response?.status == HttpStatuses.Ok) {
      const fileId: number = (e.affectedFiles[0] as any)?.fileId;
      if (fileId) {
        setForm(
          produce((draft: any) => {
            draft[option.id] = draft[option.id].filter(
              (v: number) => v != fileId,
            );
          }),
        );
      }
    }
  };

  return (
    <FileInput
      label={getOptionLabel(option.title, option.required)}
      aditionalInformation={option.aditionalInformation}
      isProductDocumentation={option.isProductDocumentation}
      onStatusChange={onStatusChange}
      onBeforeRemove={onBeforeRemove}
      onRemove={onRemove}
      batch={false}
      defaultFiles={[]}
      multiple={true}
      withCredentials={false}
      saveUrl={`${config.benefitApiUrl}/products/${productId}/options/${option.id}/file`}
      removeUrl={`${config.benefitApiUrl}/products/${productId}/options/${option.id}/file`}
      removeMethod="DELETE"
      saveField="file"
      errors={errors?.[option.id]}
      disabled={option.isReadonly}
    />
  );
};

export const renderTextOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  return (
    <Input
      onChange={({ target: { value } }) => {
        setForm(
          produce((draft: any) => {
            draft[option.id] = value;
          }),
        );
      }}
      value={form[option.id]}
      label={getOptionLabel(option.title, option.required)}
      errors={errors?.[option.id]}
      clearErrors={() => clearErrors?.(option.id)}
      disabled={option.isReadonly}
    />
  );
};

export const renderTextAreaOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  return (
    <TextArea
      onChange={({ target: { value } }) => {
        setForm(
          produce((draft: any) => {
            draft[option.id] = value;
          }),
        );
      }}
      value={form[option.id]}
      label={getOptionLabel(option.title, option.required)}
      errors={errors?.[option.id]}
      clearErrors={() => clearErrors?.(option.id)}
      disabled={option.isReadonly}
    />
  );
};

export const renderRadioOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  data: RadioButtonProps[],
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  return (
    <RadioGroup
      onChange={e => {
        setForm(
          produce((draft: any) => {
            draft[option.id] = e.value;
          }),
        );
      }}
      value={form[option.id]}
      label={getOptionLabel(option.title, option.required)}
      data={data}
      errors={errors?.[option.id]}
      clearErrors={() => clearErrors?.(option.id)}
      disabled={option.isReadonly}
    />
  );
};

export const renderDateOption = (
  setForm: any,
  option: ProductOption,
  form: Dictionary<any>,
  errors?: ErrorState,
  clearErrors?: (optionId: number) => void,
) => {
  return (
    <DatePicker
      onChange={value => {
        setForm(
          produce((draft: any) => {
            draft[option.id] = value;
          }),
        );
      }}
      value={form[option.id]}
      label={getOptionLabel(option.title, option.required)}
      errors={errors?.[option.id]}
      clearErrors={() => clearErrors?.(option.id)}
      disabled={option.isReadonly}
    />
  );
};

interface OptionsFormProps {
  options: ProductOption[];
  form: Dictionary<any>;
  setForm: Dispatch<SetStateAction<Dictionary<any>>>;
  errors: ErrorState;
  setErrors: Dispatch<SetStateAction<ErrorState>>;
  clearErrors: (optionId: number | string) => void;
  productId: number;
}

export const OptionsForm = (props: OptionsFormProps) => {
  const { options, form, setForm, errors, setErrors, clearErrors, productId } =
    props;
  return (
    <>
      {options
        .filter(e => e.id !== cardFormFactorOptionId)
        .map((option, i) => {
          return (
            <div
              className={`products-option ${
                option.type == OptionType.Checkbox
                  ? 'products-options-checkbox'
                  : ''
              }`}
              key={i}
            >
              {option.type == OptionType.Text &&
                renderTextOption(setForm, option, form, errors, clearErrors)}
              {option.type == OptionType.TextArea &&
                renderTextAreaOption(
                  setForm,
                  option,
                  form,
                  errors,
                  clearErrors,
                )}
              {option.type == OptionType.Checkbox &&
                renderCheckBoxOption(
                  setForm,
                  option,
                  form,
                  errors,
                  clearErrors,
                )}
              {option.type == OptionType.Select &&
                renderSelectOption(setForm, option, form, errors, clearErrors)}
              {option.type == OptionType.Radio &&
                renderRadioOption(
                  setForm,
                  option,
                  form,
                  option.values.map(e => ({
                    label: e.name,
                    value: e.value,
                  })),
                  errors,
                  clearErrors,
                )}
              {option.type == OptionType.Date &&
                renderDateOption(
                  setForm,
                  option,
                  form,
                  props.errors,
                  clearErrors,
                )}
              {option.type == OptionType.File &&
                renderFileOption(
                  setForm,
                  option,
                  productId,
                  errors,
                  setErrors,
                  clearErrors,
                )}
            </div>
          );
        })}
    </>
  );
};
